import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  PageHeader,
  Card,
  DataTable,
  Badge,
  Button,
  SearchInput,
  Toast,
  EmptyState,
} from '../ui';
import useDebouncedValue from '../../hooks/useDebouncedValue';
import Drawer from './Drawer';
import {
  listNameRebuild,
  listFamilies,
  createNameRebuildJob,
  reviewNameRebuild,
  applyNameRebuild,
  subscribeToEnrichJobStream,
} from '../../services/pimApi';
import { listTagGroups } from '../../services/tagsApi';
import './pim.css';

const STATUS_BADGES = {
  not_generated: { variant: 'neutral', label: 'Not generated' },
  pending: { variant: 'warning', label: 'Pending review' },
  approved: { variant: 'info', label: 'Approved' },
  rejected: { variant: 'neutral', label: 'Rejected' },
  applied: { variant: 'success', label: 'Applied' },
  error: { variant: 'danger', label: 'Error' },
};

const PAGE_SIZES = [50, 100, 250, 500, 1000];

// Mirrors MAX_SKU_LIST / MAX_FILTER_LIMIT in waterco.pim.name_rebuild; the API
// rejects anything above it.
const MAX_JOB_SKUS = 1000;

// One Gemini call per SKU, plus the inter-call delay: about six seconds each in
// practice. Worth stating up front now that a job can run for hours.
const SECONDS_PER_SKU = 6;

// Mirrors BLOCKING_FLAGS in waterco.pim.name_rebuild: the vocab_unmatched keys
// that hold up a bulk approve. An off-list family or product_type is advisory
// only — the vocabulary always trails the catalogue.
const BLOCKING_FLAGS = ['dropped_words', 'abbreviation'];

function estimateRuntime(count) {
  const minutes = Math.round((count * SECONDS_PER_SKU) / 60);
  if (minutes < 1) return 'under a minute';
  if (minutes < 60) return `about ${minutes} minute${minutes === 1 ? '' : 's'}`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return `about ${hours}h${rest ? ` ${rest}m` : ''}`;
}

// Filter chip order; labels reuse STATUS_BADGES.
const STATUS_FILTERS = ['not_generated', 'pending', 'approved', 'rejected', 'applied', 'error'];

const EMPTY_HINTS = {
  not_generated: 'Every product has a proposal — generate more via "Generate all".',
  pending: 'No proposals awaiting review. Generate proposals to get started.',
  approved: 'No approved proposals yet — select pending rows and click Approve.',
  rejected: 'No rejected proposals.',
  applied: 'No proposals have been applied yet — approve rows, then click Apply approved.',
  error: 'No generation errors.',
};

function StatusBadge({ status, errorDetail }) {
  const cfg = STATUS_BADGES[status] || STATUS_BADGES.not_generated;
  return (
    <span title={status === 'error' ? errorDetail || '' : undefined}>
      <Badge variant={cfg.variant}>{cfg.label}</Badge>
    </span>
  );
}

function RebuildProgressCard({ progress, jobId }) {
  const c = progress?.counters || {};
  return (
    <Card style={{ marginBottom: 12 }}>
      <p style={{ margin: '0 0 8px' }}>
        Name rebuild job #{jobId}: <strong>{progress?.status || 'running'}</strong>
        {c.current_sku ? ` · ${c.current_sku}` : ''}
      </p>
      <p style={{ margin: 0, fontSize: 13, color: '#667' }}>
        {typeof c.processed === 'number' ? `${c.processed}/${c.eligible ?? '?'} processed` : 'Starting…'}
        {typeof c.enriched === 'number' ? ` · ${c.enriched} generated` : ''}
        {typeof c.skipped === 'number' && c.skipped ? ` · ${c.skipped} skipped` : ''}
        {typeof c.errors === 'number' && c.errors ? ` · ${c.errors} errors` : ''}
      </p>
    </Card>
  );
}

export default function NameRebuild() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [statusCounts, setStatusCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [qDraft, setQDraft] = useState('');
  const q = useDebouncedValue(qDraft);
  const [familyCode, setFamilyCode] = useState('');
  const [status, setStatus] = useState('');
  const [families, setFamilies] = useState([]);
  // Tag spec ("trade_channel:swimart"). The same value scopes the grid and the
  // generation job, so "Generate by tag" always covers what is on screen.
  const [tag, setTag] = useState('');
  const [tagGroups, setTagGroups] = useState([]);
  const [offset, setOffset] = useState(0);
  const [toast, setToast] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkModal, setBulkModal] = useState(null);
  const [bulkLimit, setBulkLimit] = useState(100);
  const [job, setJob] = useState(null);
  const [jobProgress, setJobProgress] = useState(null);
  // sku -> edited B2B / customer name (inline edit before approving).
  const [edits, setEdits] = useState({});
  const [customerEdits, setCustomerEdits] = useState({});
  const [busy, setBusy] = useState(false);
  const [limit, setLimit] = useState(PAGE_SIZES[0]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit, offset };
      if (q) params.q = q;
      if (familyCode) params.family_code = familyCode;
      if (status) params.status = status;
      if (tag) params.tag = tag;
      const data = await listNameRebuild(params);
      setRows(data.items);
      setTotal(data.total);
      setStatusCounts(data.status_counts || {});
      setSelected(new Set());
      setEdits({});
      setCustomerEdits({});
    } catch (e) {
      setToast({ message: 'Failed to load name rebuild grid', error: true });
    } finally {
      setLoading(false);
    }
  }, [q, familyCode, status, tag, offset, limit]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    listFamilies().then(setFamilies).catch(() => {});
    listTagGroups({ entity_type: 'product' }).then(setTagGroups).catch(() => {});
  }, []);

  const approvedCount = statusCounts.approved ?? 0;

  const pageSkus = useMemo(() => rows.map((r) => r.sku), [rows]);

  // Selected rows a bulk approve will refuse: only the flags meaning the name
  // may be wrong, not an off-list term. Counted from the loaded page, so a
  // selection spanning pages under-counts and the toast reports the rest.
  const flaggedSelectedCount = useMemo(
    () =>
      rows.filter(
        (r) =>
          selected.has(r.sku) &&
          BLOCKING_FLAGS.some((flag) => (r.vocab_unmatched || {})[flag])
      ).length,
    [rows, selected]
  );
  const allPageSelected = pageSkus.length > 0 && pageSkus.every((s) => selected.has(s));

  const toggleRow = (sku) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(sku)) next.delete(sku);
      else next.add(sku);
      return next;
    });
  };

  const toggleAllPage = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allPageSelected) pageSkus.forEach((s) => next.delete(s));
      else pageSkus.forEach((s) => next.add(s));
      return next;
    });
  };

  const startJob = async (payload) => {
    try {
      const created = await createNameRebuildJob(payload);
      setJob(created.id);
      setJobProgress({ status: 'pending', counters: {} });
      subscribeToEnrichJobStream(
        created.id,
        (p) => {
          setJobProgress(p);
          if (['succeeded', 'failed', 'cancelled'].includes(p?.status)) {
            load();
          }
        },
        () => {}
      );
      setToast({ message: `Name rebuild job #${created.id} started` });
    } catch (e) {
      setToast({ message: e?.response?.data?.detail || 'Failed to start name rebuild job', error: true });
    }
  };

  const generateSelected = () => {
    if (selected.size === 0) return;
    if (
      !window.confirm(
        `Generate structured name proposals for ${selected.size} selected SKU(s)?` +
          ` Uses Gemini (API cost applies) and runs for ${estimateRuntime(selected.size)}.` +
          ' Existing proposals for these SKUs are replaced.'
      )
    ) {
      return;
    }
    startJob({ skus: Array.from(selected) });
  };

  // A tag filter on the grid scopes the job too, so "Generate" never silently
  // covers more products than the operator is looking at.
  const confirmBulkGenerate = () => {
    setBulkModal(null);
    startJob({
      filter: tag ? 'by_tag' : 'all_enabled',
      tag: tag || undefined,
      limit: Math.min(MAX_JOB_SKUS, Math.max(1, Number(bulkLimit) || 100)),
    });
  };

  const reviewSelected = async (action, { includeFlagged = false } = {}) => {
    if (selected.size === 0 || busy) return;
    setBusy(true);
    try {
      const skus = Array.from(selected);
      // A single selected row with inline edits sends the edited name(s) along
      // with the approval; bulk actions never carry edits.
      const payload = { skus, action };
      if (includeFlagged) payload.include_flagged = true;
      if (action === 'approve' && skus.length === 1) {
        const sku = skus[0];
        if (edits[sku] !== undefined) {
          const edited = edits[sku].trim();
          if (!edited) {
            setToast({ message: 'Edited product name is empty', error: true });
            setBusy(false);
            return;
          }
          payload.proposed_name = edited;
        }
        if (customerEdits[sku] !== undefined) {
          const edited = customerEdits[sku].trim();
          if (!edited) {
            setToast({ message: 'Edited customer name is empty', error: true });
            setBusy(false);
            return;
          }
          payload.proposed_customer_name = edited;
        }
      }
      const res = await reviewNameRebuild(payload);
      const skipped = res.skipped?.length
        ? ` (${res.skipped.length} skipped — already applied, missing a proposal, needs` +
          ' regeneration, or flagged as possibly wrong: use "Approve flagged too")'
        : '';
      setToast({ message: `${res.updated.length} ${action === 'approve' ? 'approved' : 'rejected'}${skipped}` });
      load();
    } catch (e) {
      setToast({ message: e?.response?.data?.detail || `Failed to ${action}`, error: true });
    } finally {
      setBusy(false);
    }
  };

  const applyApproved = async (onlySelected) => {
    if (busy) return;
    const scope = onlySelected ? `${selected.size} selected` : 'ALL approved';
    if (
      !window.confirm(
        `Apply ${scope} name(s) to the PIM? This overwrites the Product Name (B2B, no brand) and Customer Name (with brand) on every channel and locks the product name against inbound sync overwrites.`
      )
    ) {
      return;
    }
    setBusy(true);
    try {
      const res = await applyNameRebuild(onlySelected ? { skus: Array.from(selected) } : {});
      if (res.applied.length === 0 && !res.errors?.length) {
        setToast({
          message: 'Nothing applied — no approved proposals. Approve rows first.',
          error: true,
        });
      } else {
        const errors = res.errors?.length ? ` · ${res.errors.length} failed` : '';
        setToast({ message: `${res.applied.length} name(s) applied${errors}`, error: Boolean(res.errors?.length) });
      }
      load();
    } catch (e) {
      setToast({ message: e?.response?.data?.detail || 'Apply failed', error: true });
    } finally {
      setBusy(false);
    }
  };

  const columns = [
    {
      key: 'select',
      header: (
        <input type="checkbox" checked={allPageSelected} onChange={toggleAllPage} aria-label="Select all on page" />
      ),
      render: (r) => (
        <input
          type="checkbox"
          checked={selected.has(r.sku)}
          onChange={() => toggleRow(r.sku)}
          aria-label={`Select ${r.sku}`}
        />
      ),
    },
    {
      key: 'sku',
      header: 'SKU',
      render: (r) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <code>{r.sku}</code>
          {r.name_locked && (
            <span title="Applied name is protected from inbound syncs">
              <Badge variant="info">Locked</Badge>
            </span>
          )}
        </span>
      ),
    },
    {
      key: 'epicor_name',
      header: 'Epicor name',
      render: (r) => r.epicor_name || <span style={{ color: '#99a' }}>—</span>,
    },
    {
      key: 'epicor_technical_description',
      header: 'Technical description',
      render: (r) => {
        const tech = (r.epicor_technical_description || '').trim();
        if (!tech) return <span style={{ color: '#99a' }}>—</span>;
        // Around half the catalogue copies the name into this field, where it
        // adds nothing to the parse. Saying so is more use than showing the
        // duplicate, and explains why it did not influence the proposal.
        const squash = (s) => s.replace(/\s+/g, ' ').trim().toLowerCase();
        if (squash(tech) === squash(r.epicor_name || '')) {
          return (
            <span
              style={{ color: '#99a', fontStyle: 'italic' }}
              title="This field repeats the Epicor name, so it adds nothing to the parse and is not used as a hint."
            >
              Same as Epicor name
            </span>
          );
        }
        // Text that the hint gate rejected (a procurement note, a bare
        // repetition of the SKU) did not shape the proposal, so it is dimmed
        // rather than shown as though it had.
        if (!r.technical_hint_used) {
          return (
            <span
              style={{ color: '#99a' }}
              title={`Not used as a hint — it does not describe the product itself. Full text: ${tech}`}
            >
              {tech}
            </span>
          );
        }
        return <span title={tech}>{tech}</span>;
      },
    },
    {
      key: 'current_product_name',
      header: 'Current name',
      render: (r) => r.current_product_name || <span style={{ color: '#99a' }}>—</span>,
    },
    {
      key: 'proposed_name',
      header: 'New product name (B2B)',
      render: (r) => {
        if (r.status === 'error') {
          return <span style={{ color: '#c33', fontSize: 13 }}>{r.error_detail || 'Generation failed'}</span>;
        }
        if (!r.proposed_name && edits[r.sku] === undefined) {
          return <span style={{ color: '#99a' }}>—</span>;
        }
        const editable = ['pending', 'approved', 'rejected'].includes(r.status);
        if (!editable) return r.proposed_name;
        return (
          <input
            type="text"
            maxLength={75}
            value={edits[r.sku] !== undefined ? edits[r.sku] : r.proposed_name || ''}
            onChange={(e) => setEdits((prev) => ({ ...prev, [r.sku]: e.target.value }))}
            style={{ width: '100%', minWidth: 220, fontSize: 13 }}
            aria-label={`Proposed product name for ${r.sku}`}
          />
        );
      },
    },
    {
      key: 'proposed_customer_name',
      header: 'New customer name',
      render: (r) => {
        if (r.status === 'error') {
          return <span style={{ color: '#99a' }}>—</span>;
        }
        if (!r.proposed_customer_name && customerEdits[r.sku] === undefined) {
          // Row generated before the dual-name change: must be regenerated.
          if (r.proposed_name) {
            return (
              <span style={{ color: '#b7791f', fontSize: 13 }} title="Generated before customer names existed — regenerate this SKU">
                Regenerate needed
              </span>
            );
          }
          return <span style={{ color: '#99a' }}>—</span>;
        }
        const editable = ['pending', 'approved', 'rejected'].includes(r.status);
        if (!editable) return r.proposed_customer_name;
        return (
          <input
            type="text"
            maxLength={75}
            value={
              customerEdits[r.sku] !== undefined
                ? customerEdits[r.sku]
                : r.proposed_customer_name || ''
            }
            onChange={(e) => setCustomerEdits((prev) => ({ ...prev, [r.sku]: e.target.value }))}
            style={{ width: '100%', minWidth: 220, fontSize: 13 }}
            aria-label={`Proposed customer name for ${r.sku}`}
          />
        );
      },
    },
    { key: 'family_code', header: 'Family', render: (r) => r.family_code || '—' },
    {
      key: 'status',
      header: 'Status',
      render: (r) => {
        // vocab_unmatched carries three kinds of flag. Two mean the name may be
        // wrong and hold up a bulk approve: words the parse discarded
        // ('dropped_words') and an abbreviation it refused to guess
        // ('abbreviation'). An off-list family or product_type is advisory —
        // worth a look, but the vocabulary always trails the catalogue.
        const flags = Object.entries(r.vocab_unmatched || {});
        const dropped = flags.filter(([slot]) => slot === 'dropped_words');
        const abbreviations = flags.filter(([slot]) => slot === 'abbreviation');
        const offList = flags.filter(
          ([slot]) => slot !== 'abbreviation' && slot !== 'dropped_words'
        );
        const tips = [
          dropped.length > 0 &&
            `The Epicor name contains ${dropped
              .map(([, value]) => value)
              .join(', ')}, which did not make it into this name.` +
              ' Check whether those words identify the part before approving.',
          offList.length > 0 &&
            `Not in the approved vocabulary yet: ${offList
              .map(([slot, value]) => `${slot} = "${value}"`)
              .join(', ')}. This does not block approval — worth adding to the vocabulary` +
              ' so the term stays consistent next time.',
          abbreviations.length > 0 &&
            `Could not resolve ${abbreviations
              .map(([, value]) => value)
              .join(', ')} from the catalogue, so it was left as written rather than guessed.` +
              ' Expand it yourself before approving.',
        ].filter(Boolean);
        // Lead with the most consequential problem: a missing word changes which
        // part the name describes.
        const label =
          (dropped.length > 0 && 'Words dropped') ||
          (offList.length > 0 && 'Off-list') ||
          'Check abbrev.';
        return (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <StatusBadge status={r.status} errorDetail={r.error_detail} />
            {tips.length > 0 && (
              <span title={tips.join(' ')}>
                <Badge variant={dropped.length > 0 ? 'danger' : 'warning'}>{label}</Badge>
              </span>
            )}
          </span>
        );
      },
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (r) => (
        <Button size="sm" variant="secondary" to={`/pim/products/${encodeURIComponent(r.sku)}`}>
          Edit
        </Button>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Name rebuild"
        sub="Structured renaming: parse legacy Epicor names into convention components, review, then apply."
        actions={
          <>
            <Button variant="secondary" onClick={() => setBulkModal('generate')}>
              {tag ? 'Generate tagged' : 'Generate all'}
            </Button>
            <span
              title={
                approvedCount === 0
                  ? 'No approved proposals — select pending rows and click Approve first'
                  : undefined
              }
            >
              <Button onClick={() => applyApproved(false)} disabled={busy || approvedCount === 0}>
                Apply approved{typeof approvedCount === 'number' ? ` (${approvedCount})` : ''}
              </Button>
            </span>
          </>
        }
      />

      {job && jobProgress && <RebuildProgressCard progress={jobProgress} jobId={job} />}

      {selected.size > 0 && (
        <div className="pim-bulk-bar" style={{ position: 'sticky', top: 0, zIndex: 5, marginBottom: 12 }}>
          <Card>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              <strong>{selected.size} selected</strong>
              <Button size="sm" onClick={generateSelected} disabled={busy}>
                Generate ({selected.size})
              </Button>
              <Button size="sm" variant="secondary" onClick={() => reviewSelected('approve')} disabled={busy}>
                Approve
              </Button>
              {flaggedSelectedCount > 0 && (
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={busy}
                  title={
                    `${flaggedSelectedCount} of the selected rows are flagged as possibly wrong` +
                    ' (a word was dropped, or an abbreviation could not be resolved).' +
                    ' Approve includes them only via this button.'
                  }
                  onClick={() => {
                    if (
                      window.confirm(
                        `${flaggedSelectedCount} of the ${selected.size} selected rows are flagged as` +
                          ' possibly wrong — a word from the Epicor name is missing, or an' +
                          ' abbreviation could not be resolved. Approve them anyway?'
                      )
                    ) {
                      reviewSelected('approve', { includeFlagged: true });
                    }
                  }}
                >
                  Approve flagged too ({flaggedSelectedCount})
                </Button>
              )}
              <Button size="sm" variant="secondary" onClick={() => reviewSelected('reject')} disabled={busy}>
                Reject
              </Button>
              <Button size="sm" variant="secondary" onClick={() => applyApproved(true)} disabled={busy}>
                Apply approved (selected)
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())}>Clear</Button>
            </div>
          </Card>
        </div>
      )}

      <Card flush>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', padding: 14, alignItems: 'center' }}>
          <SearchInput
            value={qDraft}
            onChange={(v) => { setOffset(0); setQDraft(v); }}
            placeholder="SKU, Epicor name or new name — wildcards * and ?"
            style={{ minWidth: 380 }}
          />
          <select value={familyCode} onChange={(e) => { setOffset(0); setFamilyCode(e.target.value); }}>
            <option value="">All families</option>
            {families.map((f) => (
              <option key={f.code} value={f.code}>{f.label}</option>
            ))}
          </select>
          {tagGroups.some((g) => (g.values || []).length > 0) && (
            <select value={tag} onChange={(e) => { setOffset(0); setTag(e.target.value); }}>
              <option value="">Any tag</option>
              {tagGroups.map((group) => (
                <optgroup key={group.id} label={group.label}>
                  {(group.values || []).map((value) => (
                    <option key={value.id} value={`${group.code}:${value.code}`}>
                      {value.label}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          )}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            <button
              type="button"
              className={`pim-tag-chip${status === '' ? ' active' : ''}`}
              onClick={() => { setOffset(0); setStatus(''); }}
            >
              All
            </button>
            {STATUS_FILTERS.map((s) => {
              const n = statusCounts[s];
              return (
                <button
                  type="button"
                  key={s}
                  className={`pim-tag-chip${status === s ? ' active' : ''}`}
                  onClick={() => { setOffset(0); setStatus(s); }}
                >
                  {STATUS_BADGES[s].label}
                  {typeof n === 'number' ? ` (${n})` : ''}
                </button>
              );
            })}
          </div>
        </div>
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={(r) => r.sku}
          loading={loading}
          empty={
            <EmptyState
              title={status ? `No ${STATUS_BADGES[status].label.toLowerCase()} rows` : 'Nothing here yet'}
              body={
                (status && EMPTY_HINTS[status]) || 'No rows match the current filters.'
              }
            />
          }
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 14 }}>
          <span style={{ fontSize: 13, color: '#667' }}>
            {total} products · showing {total ? offset + 1 : 0}–{Math.min(offset + limit, total)}
            {job ? (
              <> · <Link to={`/jobs/history?queue=pim-enrich`}>View jobs</Link></>
            ) : null}
          </span>
          <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13, color: '#667' }}>
              Rows
              <select
                value={limit}
                onChange={(e) => { setOffset(0); setLimit(Number(e.target.value)); }}
              >
                {PAGE_SIZES.map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </label>
            <Button variant="secondary" size="sm" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - limit))}>
              Prev
            </Button>
            <Button variant="secondary" size="sm" disabled={offset + limit >= total} onClick={() => setOffset(offset + limit)}>
              Next
            </Button>
          </span>
        </div>
      </Card>

      {bulkModal === 'generate' && (
        <Drawer
          title="Generate name proposals"
          sub="Parses each legacy Epicor name into convention components with Gemini and assembles the new name."
          onClose={() => setBulkModal(null)}
          footer={
            <>
              <Button variant="secondary" onClick={() => setBulkModal(null)}>Cancel</Button>
              <Button onClick={confirmBulkGenerate}>Start job</Button>
            </>
          }
        >
          <div className="pim-field">
            <label>Limit (max {MAX_JOB_SKUS.toLocaleString()})</label>
            <input
              type="number"
              min={1}
              max={MAX_JOB_SKUS}
              value={bulkLimit}
              onChange={(e) => setBulkLimit(e.target.value)}
            />
            <p className="pim-muted" style={{ marginTop: 6 }}>
              Runs for{' '}
              {estimateRuntime(
                Math.min(MAX_JOB_SKUS, Math.max(1, Number(bulkLimit) || 100))
              )}
              , one Gemini call per SKU. The job can be cancelled while it runs.
            </p>
          </div>
          <p className="pim-muted" style={{ marginTop: 12 }}>
            Targets enabled products with a seed name
            {tag ? ` carrying the ${tag.split(':').pop()} tag` : ''}. SKUs whose proposal was
            already applied are skipped (select them explicitly to regenerate). Gemini charges
            apply; nothing is written to the live product name until a proposal is approved and
            applied.
          </p>
        </Drawer>
      )}

      <Toast message={toast?.message} error={toast?.error} />
    </>
  );
}
